import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { termsService } from "../services";
import { STORAGE_KEYS } from "../types/storage";
import { TermsListItem } from "../types/terms";

// 프론트엔드 약관 동의 상태 타입
export interface FrontendAgreements {
  ageOverFourteenAgreement: boolean; // 만 14세 입니다.
  aiHomedoctorServiceAgreement: boolean; // AI홈닥터 서비스이용약관 동의
  marketingInfoCollectionAgreement: boolean; // 마케팅 목적 개인정보 수집 및 이용 동의
  marketingInfoReceiptAgreement: boolean; // 마케팅정보 수신동의
  personalInfoAgreement: boolean; // 개인정보수집 및 이용동의
}

export type FrontendAgreementType = keyof FrontendAgreements;

// 서버 약관 동의 상태 타입
export interface ServerAgreement {
  termsNo: string;
  agreed: boolean;
}

interface UseTermsAgreementsReturn {
  // 데이터
  termsItems: TermsListItem[];
  datasetTerms: TermsListItem[];
  frontendAgreements: FrontendAgreements;
  serverAgreements: ServerAgreement[];

  // 상태
  isLoading: boolean;
  isAllRequiredAgreed: boolean;
  error: string | null;

  // 핸들러
  handleFrontendAgreementChange: (
    type: FrontendAgreementType,
    agreed: boolean
  ) => void;
  handleServerAgreementChange: (termsNo: string, agreed: boolean) => void;
  handleAgreeAll: () => void;
}

export const useTermsAgreements = (): UseTermsAgreementsReturn => {
  const navigate = useNavigate();
  // 서버에서 받아온 약관 데이터
  const [termsItems, setTermsItems] = useState<TermsListItem[]>([]);
  const [datasetTerms, setDatasetTerms] = useState<TermsListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 프론트엔드 약관 동의 상태
  // personalInfoAgreement은 항상 동의로 처리 (AI홈닥터 개인정보 수집 및 이용 동의)
  const [frontendAgreements, setFrontendAgreements] =
    useState<FrontendAgreements>({
      ageOverFourteenAgreement: false,
      aiHomedoctorServiceAgreement: false,
      marketingInfoCollectionAgreement: false,
      marketingInfoReceiptAgreement: false,
      personalInfoAgreement: true, // 항상 동의
    });

  // 서버 약관 동의 상태
  const [serverAgreements, setServerAgreements] = useState<ServerAgreement[]>(
    []
  );


  useEffect(() => {
    // 세션에서 본인인증 ID 확인 (다날 본인인증 기반 회원가입 플로우)
    const identityVerificationId = sessionStorage.getItem(STORAGE_KEYS.VERIFICATION_ID);
    if (!identityVerificationId) {
      // 본인인증 ID가 없으면 다시 본인인증 페이지로
      navigate("/auth/identity-verification");
      return;
    }
  }, [navigate]);

  // 약관 데이터 로드
  useEffect(() => {
    const loadTerms = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [termsData, datasetData] = await Promise.all([
          termsService.getTermsList(false),
          termsService.getTermsList(true),
        ]);

        // termsNo 기준으로 오름차순 정렬
        const sortedTerms = termsData.sort(
          (a, b) => Number(a.termsNo) - Number(b.termsNo)
        );
        setTermsItems(sortedTerms);
        setDatasetTerms(datasetData);

        // 초기 동의 상태 설정 (모든 약관 false)
        const allTerms = [...sortedTerms, ...datasetData];
        const initialAgreements = allTerms.map((item) => ({
          termsNo: item.termsNo,
          agreed: false,
        }));
        setServerAgreements(initialAgreements);
      } catch (err) {
        console.error("Terms loading error:", err);
        setError("약관 정보를 불러오는데 실패했습니다. 다시 시도해주세요.");
      } finally {
        setIsLoading(false);
      }
    };

    loadTerms();
  }, []);

  // 모든 필수 약관 동의 상태 계산
  const isAllRequiredAgreed = useMemo(() => {
    // 프론트엔드 필수 약관 체크
    const allFrontendRequiredAgreed =
      frontendAgreements.ageOverFourteenAgreement &&
      frontendAgreements.aiHomedoctorServiceAgreement &&
      frontendAgreements.personalInfoAgreement;

    // 서버 약관 체크 (의료마이데이터)
    const allServerTermsAgreed =
      termsItems.length === 0 ||
      termsItems.every((term) =>
        serverAgreements.find((a) => a.termsNo === term.termsNo && a.agreed)
      );

    // 데이터셋 약관 체크 (공공데이터)
    const allDatasetTermsAgreed =
      datasetTerms.length === 0 ||
      datasetTerms.every((term) =>
        serverAgreements.find((a) => a.termsNo === term.termsNo && a.agreed)
      );

    return (
      allFrontendRequiredAgreed && allServerTermsAgreed && allDatasetTermsAgreed
    );
  }, [frontendAgreements, serverAgreements, termsItems, datasetTerms]);

  // 프론트엔드 약관 개별 변경
  const handleFrontendAgreementChange = useCallback(
    (type: FrontendAgreementType, agreed: boolean) => {
      setFrontendAgreements((prev) => ({
        ...prev,
        [type]: agreed,
      }));
    },
    []
  );

  // 서버 약관 개별 변경
  const handleServerAgreementChange = useCallback(
    (termsNo: string, agreed: boolean) => {
      setServerAgreements((prev) =>
        prev.map((agreement) =>
          agreement.termsNo === termsNo ? { ...agreement, agreed } : agreement
        )
      );
    },
    []
  );

  // 모두 동의하기
  const handleAgreeAll = useCallback(() => {
    // 프론트엔드 약관 모두 동의
    setFrontendAgreements({
      ageOverFourteenAgreement: true,
      aiHomedoctorServiceAgreement: true,
      marketingInfoCollectionAgreement: true,
      marketingInfoReceiptAgreement: true,
      personalInfoAgreement: true,
    });

    // 서버 약관 모두 동의
    setServerAgreements((prev) =>
      prev.map((agreement) => ({ ...agreement, agreed: true }))
    );
  }, []);

  return {
    // 데이터
    termsItems,
    datasetTerms,
    frontendAgreements,
    serverAgreements,

    // 상태
    isLoading,
    isAllRequiredAgreed,
    error,

    // 핸들러
    handleFrontendAgreementChange,
    handleServerAgreementChange,
    handleAgreeAll,
  };
};
